// 핵심 과제 현황 — 사업별로 라벨(성과지표)이 붙은 세부실행과제만 모아 진행률을 본다
// 같은 라벨은 한 칸으로 묶고 라벨명 오름차순으로 보인다.
// 줄을 누르면 세부실행과제 상세(수정 가능), 보고서 버튼은 과제/업무 관리와 같은 보고서 패널을 연다.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Calculator, FileText } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { useAppSelector } from '@/store/hooks'
import { extractErrorMessage } from '@/utils/api-error-handler'
import { TaskSummaryService } from '@/api/generated/services/TaskSummaryService'
import { TaskReportService } from '@/api'
import type { TaskSummaryKeyTasksResponse } from '@/api/generated/models/TaskSummaryKeyTasksResponse'
import type { KeyTaskRowItem } from '@/api/generated/models/KeyTaskRowItem'
import type { KeyTaskBusinessItem } from '@/api/generated/models/KeyTaskBusinessItem'
import type { KeyTaskLabelStat } from '@/api/generated/models/KeyTaskLabelStat'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import TaskDetailPanel from '@/pages/project/TaskManagement/components/TaskDetailPanel'
import TaskCreatePanel from '@/pages/project/TaskManagement/components/TaskCreatePanel'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import { buildTaskEditInitialData } from '@/pages/project/TaskManagement/taskEditData'
import ReportDetailPanel from '@/pages/project/ReportManagement/components/ReportDetailPanel'
import RateBar from '../StrategyDashboard/components/RateBar'
import Chart from '@/components/data/Chart'
import LabelFormulaPanel from './components/LabelFormulaPanel'
import {
  formatNumber,
  formatRate,
  getRateColor,
  strategyColors,
} from '../StrategyDashboard/strategyMetrics'

const font = designTokens.typography.fontFamily.sans[0]

const COLUMNS = [
  '관련 성과지표(라벨명)',
  '전략과제',
  '실행과제',
  '세부실행과제',
  '과제 진행률',
  '예산 집행률',
  '성과 달성률',
  '',
]
const GRID_TEMPLATE =
  'minmax(250px, 1.3fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(190px, 1.3fr) repeat(3, minmax(200px, 1fr)) 100px'
const MIN_WIDTH = 1600
const CELL_PADDING = '14px 8px'

// 데이터 없음: 회색 빗금 막대
const NO_DATA_PATTERN = `repeating-linear-gradient(135deg, ${designTokens.colors.gray[2]} 0 3px, ${designTokens.colors.gray[0]} 3px 7px)`

/** 예산 금액은 길어서 만·억 단위로 줄인다 */
const formatAmount = (value: number): string => {
  if (Math.abs(value) >= 100_000_000) return `${Number((value / 100_000_000).toFixed(1))}억`
  if (Math.abs(value) >= 10_000) return `${Number((value / 10_000).toFixed(1))}만`
  return formatNumber(value)
}

interface RateValue {
  /** 해당 데이터가 없으면 null (빗금 막대) */
  rate: number | null
  /** 막대 옆 (완료/전체) 표시 */
  detail: string | null
  title?: string
}

const getRates = (row: KeyTaskRowItem): RateValue[] => {
  const item = row.task_item
  const budget = row.task_budget
  const performance = row.task_performance
  const budgetTarget = budget.total_target_figure ?? 0
  const performanceTarget = performance.total_target_figure ?? 0
  return [
    {
      rate: item.total_count ? (item.count_percentage ?? null) : null,
      detail: item.total_count ? `(${item.completed_count ?? 0}/${item.total_count})` : null,
      title: item.total_count ? '완료 수행업무 수 ÷ 전체 수행업무 수' : '등록된 수행업무 없음',
    },
    {
      rate: budgetTarget > 0 ? (budget.figure_percentage ?? null) : null,
      detail:
        budgetTarget > 0
          ? `(${formatAmount(budget.total_done_figure ?? 0)}/${formatAmount(budgetTarget)})`
          : null,
      title:
        budgetTarget > 0
          ? `집행액 ${formatNumber(budget.total_done_figure ?? 0)}원 ÷ 편성액 ${formatNumber(budgetTarget)}원`
          : '편성된 예산 없음',
    },
    {
      rate: performanceTarget > 0 ? (performance.figure_percentage ?? null) : null,
      detail:
        performanceTarget > 0
          ? `(${formatNumber(performance.total_done_figure ?? 0)}/${formatNumber(performanceTarget)})`
          : null,
      title: performanceTarget > 0 ? '달성 수치 합계 ÷ 목표 수치 합계' : '등록된 성과 없음',
    },
  ]
}

function useKeyTasks(workspaceId: string | undefined) {
  const [data, setData] = useState<TaskSummaryKeyTasksResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    setError(null)
    try {
      setData(await TaskSummaryService.appsTaskViewsGetTaskSummaryKeyTasks({ workspaceId }))
    } catch (err) {
      console.error('Failed to fetch key tasks:', err)
      setError(`핵심 과제 현황을 불러오지 못했습니다: ${extractErrorMessage(err)}`)
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

/** 라벨 하나에 묶인 세부실행과제들. 라벨이 여러 개인 과제는 각 라벨 묶음에 모두 나온다. */
interface LabelGroup {
  key: string
  rows: KeyTaskRowItem[]
}

const groupByLabel = (rows: KeyTaskRowItem[]): LabelGroup[] => {
  const groups = new Map<string, KeyTaskRowItem[]>()
  for (const row of rows) {
    for (const label of row.labels ?? []) {
      const list = groups.get(label.key) ?? []
      if (!list.some((r) => r.task_id === row.task_id)) list.push(row)
      groups.set(label.key, list)
    }
  }
  // 라벨명 오름차순 (1.1.3 < 1.1.10 처럼 숫자는 크기대로)
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'ko', { numeric: true }))
    .map(([key, groupRows]) => ({ key, rows: groupRows }))
}

// 막대와 수치를 테두리로 묶어, 옆 칸과 헷갈리지 않게 한다 (데이터 없음일 때 특히)
const RATE_BOX_STYLE = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 38,
  padding: '6px 10px',
  border: `1px solid ${strategyColors.border}`,
  borderRadius: 6,
}

function RateCell({ value }: { value: RateValue }) {
  if (value.rate == null) {
    return (
      <div title={value.title} style={{ ...RATE_BOX_STYLE, gap: 10 }}>
        <span
          aria-hidden
          style={{ display: 'block', flexGrow: 1, height: 8, borderRadius: 4, background: NO_DATA_PATTERN }}
        />
        <span style={{ fontSize: 13, color: strategyColors.mutedText, whiteSpace: 'nowrap' }}>
          데이터 없음
        </span>
      </div>
    )
  }
  return (
    <div title={value.title} style={{ ...RATE_BOX_STYLE, gap: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <RateBar rate={value.rate} />
      </div>
      {value.detail && (
        <span
          style={{
            fontSize: 12,
            color: strategyColors.subText,
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value.detail}
        </span>
      )}
    </div>
  )
}

/** 성과 수치는 0.8 같은 소수 공식 값이 있어 소수 둘째 자리까지 보인다 */
const formatFigure = (value: number): string =>
  value.toLocaleString('ko-KR', { maximumFractionDigits: 2 })

/** 라벨명 아래 지표 달성률 원형 차트 (지표 달성 계산기 수식 기준, 수식이 없으면 세부실행과제 성과 합계) */
function LabelStats({ stat }: { stat: KeyTaskLabelStat }) {
  const rate = stat.indicator_rate ?? null
  const hasFormula = Boolean(stat.target_formula || stat.done_formula)
  const title = stat.indicator_error
    ? `계산기 수식 오류 · ${stat.indicator_error}`
    : `${hasFormula ? '계산기 수식' : '수식 없음(합계)'} · 달성 ${formatFigure(stat.indicator_done_figure ?? 0)} ÷ 목표 ${formatFigure(stat.indicator_target_figure ?? 0)}`
  return (
    <div
      title={title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 12px',
        borderRadius: 8,
        background: strategyColors.naSurface,
        border: `1px solid ${strategyColors.border}`,
      }}
    >
      <div style={{ width: 96, height: 96, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {rate == null ? (
          <span
            aria-hidden
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: NO_DATA_PATTERN,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: strategyColors.naSurface }} />
          </span>
        ) : (
          <Chart
            type="radialBar"
            // 원은 100% 까지만 채우고, 가운데 글자는 실제 값(100% 초과 포함)을 보인다
            series={[Math.min(Math.max(rate, 0), 100)]}
            height={110}
            options={{
              chart: { type: 'radialBar', sparkline: { enabled: true } },
              plotOptions: {
                radialBar: {
                  hollow: { size: '58%' },
                  track: { background: strategyColors.track, strokeWidth: '100%' },
                  dataLabels: {
                    name: { show: false },
                    value: {
                      show: true,
                      offsetY: 5,
                      fontSize: '15px',
                      fontFamily: font,
                      fontWeight: 700,
                      color: strategyColors.text,
                      formatter: () => formatRate(rate),
                    },
                  },
                },
              },
              colors: [getRateColor(rate)],
              stroke: { lineCap: 'round' },
              labels: ['지표 달성률'],
            }}
          />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, fontSize: 12 }}>
        <span style={{ color: strategyColors.subText, fontWeight: 600, fontSize: 13 }}>지표 달성률</span>
        {rate == null ? (
          <span style={{ color: strategyColors.mutedText }}>{stat.indicator_error ? '수식 오류' : '데이터 없음'}</span>
        ) : (
          <span style={{ color: strategyColors.subText, fontVariantNumeric: 'tabular-nums', wordBreak: 'keep-all' }}>
            달성 {formatFigure(stat.indicator_done_figure ?? 0)} / 목표 {formatFigure(stat.indicator_target_figure ?? 0)}
          </span>
        )}
        <span style={{ color: strategyColors.mutedText, fontSize: 11 }}>
          {hasFormula ? '계산기 수식 기준' : '성과 합계 기준'}
        </span>
      </div>
    </div>
  )
}

interface BusinessSectionProps {
  business: KeyTaskBusinessItem
  onRowClick: (row: KeyTaskRowItem, business: KeyTaskBusinessItem) => void
  onReportClick: (row: KeyTaskRowItem, businessId: string) => void
  /** 기관 관리자에게만 넘긴다. 없으면 계산기 버튼을 숨긴다 */
  onOpenCalculator?: (stat: KeyTaskLabelStat) => void
}

function BusinessSection({ business, onRowClick, onReportClick, onOpenCalculator }: BusinessSectionProps) {
  const groups = useMemo(() => groupByLabel(business.rows ?? []), [business.rows])
  const statsByLabel = useMemo(
    () => new Map<string, KeyTaskLabelStat>((business.label_stats ?? []).map((stat) => [stat.label_key, stat] as const)),
    [business.label_stats]
  )
  // 한 줄이 여러 칸으로 나뉘어 있어 줄 강조를 직접 관리한다 (라벨|과제 id)
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)
  const nameStyle = { fontWeight: 700, fontSize: 15, lineHeight: 1.4, wordBreak: 'keep-all' as const }
  const taskCount = new Set((business.rows ?? []).map((row) => row.task_id)).size

  return (
    <section
      aria-label={business.business_name}
      style={{
        background: strategyColors.surface,
        border: `1px solid ${strategyColors.border}`,
        borderRadius: 12,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700 }}>{business.business_name}</h2>
        <span style={{ fontSize: 13, color: strategyColors.subText }}>
          성과지표 {groups.length}개 · 세부실행과제 {taskCount}개 · 라벨명 순 · 빗금은 데이터 없음
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: MIN_WIDTH, display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: GRID_TEMPLATE,
              fontSize: 14,
              fontWeight: 700,
              color: strategyColors.text,
              borderBottom: `1px solid ${strategyColors.border}`,
            }}
          >
            {COLUMNS.map((label, index) => (
              <span key={index} style={{ padding: '10px 8px', textAlign: 'center' }}>
                {label}
              </span>
            ))}
          </div>

          {groups.length === 0 ? (
            <div
              style={{
                padding: '24px 8px',
                fontSize: 14,
                color: strategyColors.subText,
                textAlign: 'center',
                background: strategyColors.naSurface,
              }}
            >
              등록된 성과지표가 없습니다.
            </div>
          ) : (
            groups.map((group) => (
              <div
                key={group.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: GRID_TEMPLATE,
                  borderBottom: `1px solid ${strategyColors.border}`,
                }}
              >
                {/* 같은 라벨은 한 칸으로 묶고, 라벨명 아래 라벨 전체 지표 달성률 차트를 둔다 */}
                <div
                  style={{
                    gridRow: `span ${group.rows.length}`,
                    padding: CELL_PADDING,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 10,
                    borderRight: `1px solid ${strategyColors.track}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={nameStyle}>{group.key}</span>
                    {onOpenCalculator && statsByLabel.has(group.key) && (
                      <Button
                        size="small"
                        color="white"
                        onClick={() => onOpenCalculator(statsByLabel.get(group.key) as KeyTaskLabelStat)}
                        className="flex items-center gap-1 flex-shrink-0"
                      >
                        <Calculator className="w-3 h-3" />
                        계산기
                      </Button>
                    )}
                  </div>
                  {statsByLabel.has(group.key) && (
                    <LabelStats stat={statsByLabel.get(group.key) as KeyTaskLabelStat} />
                  )}
                </div>
                {group.rows.map((row, rowIndex) => {
                  const rowKey = `${group.key}|${row.task_id}`
                  const cellStyle = {
                    padding: CELL_PADDING,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    background: hoveredKey === rowKey ? strategyColors.naSurface : undefined,
                    borderBottom:
                      rowIndex < group.rows.length - 1 ? `1px solid ${strategyColors.track}` : undefined,
                  }
                  const open = () => onRowClick(row, business)
                  return (
                    // display: contents — 칸은 바깥 grid 에 그대로 놓이고 클릭·강조만 줄 단위로 묶인다
                    <div
                      key={row.task_id}
                      style={{ display: 'contents' }}
                      onClick={open}
                      onMouseEnter={() => setHoveredKey(rowKey)}
                      onMouseLeave={() => setHoveredKey(null)}
                    >
                      <span style={{ ...nameStyle, ...cellStyle }}>{row.task_group_name}</span>
                      <span style={{ ...nameStyle, ...cellStyle }}>{row.task_sub_group_name}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            open()
                          }
                        }}
                        style={{ ...nameStyle, ...cellStyle }}
                      >
                        {row.task_name}
                      </span>
                      {getRates(row).map((value, index) => (
                        <div key={index} style={cellStyle}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <RateCell value={value} />
                          </div>
                        </div>
                      ))}
                      <div style={cellStyle} onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="small"
                          color="white"
                          onClick={() => onReportClick(row, business.business_id)}
                          className="flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          보고서
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  )
}

interface DetailTarget {
  row: KeyTaskRowItem
  business: KeyTaskBusinessItem
}

export default function KeyTaskDashboardPage() {
  const selectedWorkspace = useAppSelector((state) => state.workspace.selectedWorkspace)
  const workspaceId = selectedWorkspace?.id
  const { data, loading, error, refetch } = useKeyTasks(workspaceId)
  // 지표 달성 계산기는 기관 관리자(소유자·관리자)만 (저장 API 도 같은 권한을 검사한다)
  const orgMember = useAppSelector((state) => state.auth.orgMember)
  const isOrgAdmin = orgMember?.role === 'owner' || orgMember?.role === 'admin'
  const [calculatorStat, setCalculatorStat] = useState<KeyTaskLabelStat | null>(null)

  const [detail, setDetail] = useState<DetailTarget | null>(null)
  const [detailRefreshKey, setDetailRefreshKey] = useState(0)
  // 상세의 '수정' → 과제/업무 관리와 같은 세부실행과제 수정 패널 (상세는 열어 둔다)
  const [editTarget, setEditTarget] = useState<DetailTarget | null>(null)
  const [editData, setEditData] = useState<ReturnType<typeof buildTaskEditInitialData> | null>(null)

  const [reportTaskId, setReportTaskId] = useState<string | null>(null)
  const [reportTemplateKey, setReportTemplateKey] = useState<string | null>(null)
  const [reportTemplates, setReportTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isReportTemplatesLoading, setIsReportTemplatesLoading] = useState(false)
  const [reportTemplatesError, setReportTemplatesError] = useState<string | null>(null)

  // 보고서 패널은 상세 패널과 겹치지 않게 상세를 닫고 연다 (과제/업무 관리와 같은 동작)
  const openReport = async (row: KeyTaskRowItem, businessId: string) => {
    setDetail(null)
    setReportTemplateKey(null)
    setReportTaskId(row.task_id)
    if (!workspaceId) return
    setIsReportTemplatesLoading(true)
    setReportTemplatesError(null)
    try {
      const response = await TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
        workspaceId,
        businessId,
      })
      setReportTemplates(response || [])
    } catch (err) {
      console.error('Failed to fetch report templates:', err)
      setReportTemplatesError('보고서 템플릿을 불러오는데 실패했습니다.')
    } finally {
      setIsReportTemplatesLoading(false)
    }
  }

  const openEdit = (node: TaskTreeNode) => {
    if (!detail) return
    setEditTarget(detail)
    setEditData(buildTaskEditInitialData(node))
  }

  const closeEdit = () => {
    setEditTarget(null)
    setEditData(null)
  }

  const renderMessage = (message: string, color: string = designTokens.colors.gray[4]) => (
    <div className="p-6 h-full flex items-center justify-center">
      <div style={{ fontFamily: font, fontSize: designTokens.typography.fontSize['body-medium'][0], color }}>
        {message}
      </div>
    </div>
  )

  if (loading && !data) return renderMessage('핵심 과제 현황을 불러오는 중...')
  if (error) return renderMessage(error, designTokens.colors.error.DEFAULT)

  const businesses = data?.businesses ?? []

  return (
    <div className="p-6 h-full flex flex-col" style={{ fontFamily: font, color: strategyColors.text }}>
      <div className="flex-1 overflow-y-auto">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 24 }}>
          {businesses.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', fontSize: 16, color: strategyColors.subText }}>
              표시할 사업이 없습니다.
            </div>
          ) : (
            businesses.map((business) => (
              <BusinessSection
                key={business.business_id}
                business={business}
                onRowClick={(row, rowBusiness) => {
                  setReportTaskId(null)
                  setDetail({ row, business: rowBusiness })
                }}
                onReportClick={openReport}
                onOpenCalculator={isOrgAdmin ? setCalculatorStat : undefined}
              />
            ))
          )}
          <p style={{ margin: 0, fontSize: 12, color: strategyColors.subText }}>
            라벨명 아래 차트는 라벨 전체의 지표 달성률이며, 지표 달성 계산기 수식(비어 있으면 세부실행과제 성과 합계)으로 계산합니다 ·
            라벨이 붙은 세부실행과제만 표시합니다 · 과제 진행률: 완료 수행업무 수 ÷ 전체 수행업무 수 · 예산 집행률:
            집행액 ÷ 편성액 · 성과 달성률: 달성 수치 합계 ÷ 목표 수치 합계 · 기간 구분 없이 전체 데이터 기준
          </p>
        </div>
      </div>

      <TaskDetailPanel
        isOpen={detail != null}
        onClose={() => setDetail(null)}
        taskId={detail?.row.task_id ?? null}
        taskType={detail ? 'task' : null}
        refreshKey={detailRefreshKey}
        onEdit={openEdit}
        onSuccess={refetch}
      />
      <TaskCreatePanel
        isOpen={editTarget != null}
        onClose={closeEdit}
        taskSubgroupId={editTarget?.row.task_sub_group_id ?? ''}
        taskSubgroupName={editTarget?.row.task_sub_group_name ?? ''}
        taskGroupName={editTarget?.row.task_group_name ?? ''}
        businessName={editTarget?.business.business_name ?? ''}
        businessId={editTarget?.business.business_id ?? ''}
        onSuccess={() => {
          refetch()
          setDetailRefreshKey((key) => key + 1)
        }}
        editMode={true}
        taskId={editTarget?.row.task_id}
        initialTaskData={editData ?? undefined}
      />
      <LabelFormulaPanel
        isOpen={calculatorStat != null}
        onClose={() => setCalculatorStat(null)}
        workspaceId={workspaceId || ''}
        stat={calculatorStat}
        onSaved={refetch}
      />
      <ReportDetailPanel
        isOpen={reportTaskId != null}
        onClose={() => setReportTaskId(null)}
        taskId={reportTaskId}
        reportTemplateKey={reportTemplateKey}
        reportTemplates={reportTemplates}
        isReportTemplatesLoading={isReportTemplatesLoading}
        reportTemplatesError={reportTemplatesError}
        workspaceId={workspaceId || ''}
        onReportTypeChange={(key) => setReportTemplateKey(key)}
      />
    </div>
  )
}
